import { resolvePopulation } from '@utils/resolvePopulation';
import {
  RecurringRevenueCoverage,
  RecurringRevenueCoverageAt,
  RecurringRevenueCoverageNow,
  RecurringRevenueRow,
} from '@interfaces/reports/recurringRevenue';

/**
 * Below this share of the base, the money is DECLARED rather than shown silently.
 *
 * 97% and not 95% or 99%, from the measured distribution: coverage is 100% for the median community and
 * 98.87% at the tenth percentile, so a threshold at 95% would have declared almost nothing and one at 99%
 * would have declared a tenth of communities that are, in practice, complete. 97% sits in the gap between
 * "a rounding difference" and "a number missing a visible slice of its base".
 *
 * WHAT THIS THRESHOLD MEANS DEPENDS ON WHAT IT IS APPLIED TO, and only one of the two is a claim about money.
 *
 * For the SNAPSHOT it now runs against a share of VALUE - `mrr / (mrr + uncoveredValue)` - so the threshold finally
 * means what the copy says. That was the hole codex opened on #264 and it was real: a count share cannot bound
 * money, so two uncovered subscriptions holding the priciest plans could omit most of the value while 98 of 100
 * read as complete. `report-api#76` closed it by reporting the uncovered money itself.
 *
 * For the SERIES - `opening` and each row - it still counts SUBSCRIPTIONS, and the note beside those figures still
 * refuses to state a magnitude. Not an oversight: a subscription with no known period cannot be attributed to a
 * bucket, so the series has no denominator to pair a value with. The limit is in the data, not in this module.
 *
 * The cost of the cruder alternative was measured, and it is why a threshold exists at all: disclosing whenever
 * `covered < total` would put a banner on 100 communities holding 46,150 of 110,333 active subscribers - 42% of
 * the base, and the largest producers - to announce a ~1% omission. That is the permanent banner nobody reads,
 * bought at the price of the one that matters.
 */
/**
 * What the coverage is judged AGAINST: the snapshot it describes.
 *
 * Both fields answer the same question from different sides - is there anything here for the coverage to be
 * incomplete about - and taking them together is what lets a missing coverage be told apart from an empty one.
 */
export type SnapshotForCoverage = {
  mrr: number;
  /** Null when the response did not carry it. Read through `resolvePopulation`, never compared to zero here. */
  activeSubscribers: number | null;
};

export const DECLARE_COVERAGE_BELOW = 0.97;

/**
 * Below this share, the money is not shown at all.
 *
 * Coverage this low is not a number with a caveat, it is a different number. The worst community measured 0%
 * - every one of its subscriptions touched by a refund, a chargeback, a MED or a manual edit - and its MRR
 * would render as R$ 0,00 next to a base of real subscribers. A producer reads that as a base worth nothing
 * rather than as a figure that could not be computed, so the value is withheld and named as such.
 *
 * The point is the DIRECTION of the failure: an understated MRR looks exactly like a shrinking one.
 */
export const UNAVAILABLE_COVERAGE_BELOW = 0.5;

/**
 * What a stated share is made of, and the pair has to travel together.
 *
 * `share` and `uncoveredValue` answer DIFFERENT questions and either can be present without the other being the
 * one that decided the verdict, so `basis` says which. Without it the copy inferred the wording from
 * `uncoveredValue !== null` and mislabelled the minimum: 90 of 100 subscriptions with R$ 50 uncovered renders a
 * share of 90%, which came from the COUNT while the value coverage is about 99% - and the sentence claimed 90% of
 * the MRR. A number is not made true by the field it arrived next to. Codex, #267.
 */
/** The counts either instant carries. `total` is nullable on `current`, where absence is a real state. */
type CoverageCounts = { covered: number; total: number | null };

type MeasuredCoverage = {
  share: number;
  covered: number;
  total: number | null;
  /** Which of the two shares produced `share` - and therefore what the copy is allowed to call it. */
  basis: 'value' | 'count';
  /**
   * How much money is missing, when the API could say - an UPPER bound, so the copy must say "up to".
   *
   * Independent of `basis`: it stays a true statement even when the count decided the verdict, so the note can
   * name the amount while describing the share as a share of the base.
   */
  uncoveredValue: number | null;
};

export type MrrCoverage =
  /**
   * The whole series fell back to today's price applied backwards, because the window reaches below the
   * API's pricing floor. Not a coverage share at all - it answers a different question, and it is never
   * mixed with the priced answer inside one series.
   */
  | { kind: 'unpriced' }
  /**
   * The snapshot's coverage could not be measured in money at all, and the base is not empty.
   *
   * Reached when `uncoveredValue` is absent or unusable - an API deploy predating `report-api#76`, a partial
   * payload, or a base holding subscribers while neither side reports any money. DECLARED rather than silenced,
   * which is the whole point: not knowing how much is missing is not evidence that nothing is.
   *
   * It carries no `share`, on purpose. The count share is available and would render as "cobre 99,5% da base"
   * beside a banner saying the coverage is unknown - two claims that contradict each other, and the more
   * reassuring one is the one that would be read.
   */
  | { kind: 'unpriceable'; covered: number; total: number | null }
  /**
   * The MRR itself came back unusable - negative, or not a number at all.
   *
   * Distinct from `unpriceable`, which says the COVERAGE is unknown and is right to keep showing the figure.
   * Here the figure is the broken part, and `unpriceable` rendered it: a negative MRR printed under a warning
   * about the report being outdated, as if it were an observation. Withheld instead. Codex, #267.
   */
  | { kind: 'unusable' }
  /** Too little of the base is priced for the value to mean anything. Shown as unavailable, never as zero. */
  | ({ kind: 'unavailable' } & MeasuredCoverage)
  /** Usable, with a visible slice missing. Shown with the share stated beside it. */
  | ({ kind: 'declared' } & MeasuredCoverage)
  /** Complete, or complete enough that saying so would be noise. */
  | { kind: 'full' };

/**
 * Share of a base whose money is known, with the empty base reading as complete.
 *
 * `total === 0` returns 1 and not 0. A bucket with no active subscribers has no value to omit, so 0/0 is
 * fully covered by definition - reading it as 0% would mark every empty period as unavailable and put a
 * warning on rows that are empty for the ordinary reason that nothing was there.
 */
const shareOf = (at: CoverageCounts) =>
  typeof at.total === 'number' && at.total > 0 ? at.covered / at.total : 1;

/**
 * Share of the MONEY the snapshot speaks for, or null when it cannot be measured.
 *
 * `mrr / (mrr + uncoveredValue)`, and the two terms are the two halves of one base by construction - the API
 * measures them off the same subscriptions - so the result is in [0,1] without a guard. A part measured against a
 * separately-queried total is what made an earlier attempt at this report `covered > total` three times.
 *
 * NULL rather than a fallback share whenever an input is missing or nonsensical. Every failure here has to fail
 * LOUD, because this function's only job is deciding when to warn: a null becomes a declaration upstream, while
 * any invented number would sooner or later invent a reassuring one.
 *
 * An empty base returns 1 and not null - but only when the SNAPSHOT agrees it is empty, on all three counts.
 *
 * Zero active subscribers has no value to omit, so 0/0 is covered by definition. Anything in the snapshot
 * disagreeing is a contradiction, and all three are what version skew produces: a payload whose
 * `coverage.current` is absent gets normalised to `{ covered: 0, total: 0 }` while the snapshot arrives intact.
 * A positive MRR beside no base was the first caught; a positive `uncoveredValue` beside no base is the same
 * statement from the other side; and a positive `activeSubscribers` says the population is not empty even when
 * both money figures happen to be zero - a report that came back partial, rendering R$ 0,00 for a base that has
 * people in it. Codex, #267, three times, once per field.
 */
const valueShareOf = (
  at: RecurringRevenueCoverageNow,
  snapshot: SnapshotForCoverage,
  knownEmpty: boolean,
) => {
  const mrr = snapshot.mrr;
  const uncovered = at.uncoveredValue;
  const omitsMoney = Number.isFinite(uncovered) && (uncovered as number) > 0;
  const moneyUsable =
    Number.isFinite(uncovered) &&
    (uncovered as number) >= 0 &&
    Number.isFinite(mrr) &&
    mrr >= 0;

  /**
   * The COUNT total is not part of this sum, so it is not required to compute it.
   *
   * `mrr / (mrr + uncoveredValue)` needs the two money figures and nothing else. Gating it on a usable `total`
   * threw away a fully measurable share whenever the coverage counts were missing - R$ 1.000 counted against
   * R$ 9.000 unconfirmed is 10% coverage, and reporting that as unmeasurable SHOWS the money instead of
   * withholding it. The count is asked for below, and only for the question it does answer. Codex, #267.
   */
  const base = moneyUsable ? mrr + (uncovered as number) : 0;

  if (base > 0) return mrr / base;

  /**
   * No money on either side, so the question becomes whether there is a base at all - and that one IS the
   * count's. Nonsense in `uncoveredValue` is deliberately not a contradiction here: with no base and no MRR
   * there is nothing on the screen to qualify, and declaring would put a banner on a community that is simply
   * empty. Null included, because an absent coverage must not divide by the zero the client would have filled.
   *
   * `moneyUsable` is NOT required for this branch, and requiring it was a regression caught by three tests: an
   * absent `uncoveredValue` makes it false, so every genuinely empty community started declaring. What matters
   * here is that no money is OMITTED, which `!omitsMoney` already says; the MRR's own validity was settled
   * before any of this ran.
   */
  if (!(typeof at.total === 'number' && at.total > 0))
    return mrr === 0 && !omitsMoney && knownEmpty ? 1 : null;

  // Subscribers present and no money on either side. Nothing to measure against, and reading it as complete
  // would silence exactly the state where the money has gone missing wholesale.
  return null;
};

const classify = (
  share: number,
  at: CoverageCounts,
  basis: 'value' | 'count',
  uncoveredValue: number | null,
): MrrCoverage => {
  const measured = {
    share,
    covered: at.covered,
    total: at.total,
    basis,
    uncoveredValue,
  };

  if (share < UNAVAILABLE_COVERAGE_BELOW)
    return { kind: 'unavailable', ...measured };

  if (share < DECLARE_COVERAGE_BELOW) return { kind: 'declared', ...measured };

  return { kind: 'full' };
};

/**
 * How much of the base the MRR speaks for, at one of the two instants the report describes.
 *
 * `current` is the instant the snapshot describes - what "MRR projetado" is worth today. `opening` is the
 * start of the requested window, which is what the series is anchored on. They differ, and picking the wrong
 * one is the kind of mistake that shows a warning on the wrong figure, so the instant is named by the caller
 * rather than guessed here.
 *
 * `pricedFromPeriods` is checked ONLY for `opening`, and that is not an optimisation - it is what the flag
 * means. It says whether the SERIES priced each bucket at what the subscription was paying in it. The snapshot
 * has no such question: it is today's base at today's price, which is correct by definition rather than an
 * approximation of anything. Short-circuiting `current` on it reported the snapshot as estimated when it was not,
 * and made the two notes on the detail screen render the same paragraph twice - which is how this was caught.
 *
 * The SNAPSHOT is passed whole rather than as a bare number, and that is what stops the wrong money reaching it.
 * The covered half of the value share is `snapshot.mrr` and nothing else - a subset like `mrrAtRisk` would divide
 * by a base it is not part of and report a coverage no figure on the screen has - and taking the object means a
 * caller cannot supply one figure in place of another. It also carries the population, which is what says whether
 * an empty coverage is a genuinely empty community. Required rather than optional, so a caller adding a screen is
 * stopped by the compiler instead of silently getting the count share back; ignored for `opening`, which has no
 * value figure.
 */
export const resolveMrrCoverage = (
  coverage: RecurringRevenueCoverage,
  instant: 'current' | 'opening',
  snapshot: SnapshotForCoverage,
): MrrCoverage => {
  if (instant === 'opening' && !coverage.pricedFromPeriods)
    return { kind: 'unpriced' };

  const at = coverage[instant];
  const countShare = shareOf(at);

  // The series has no value half - an unattributable subscription has no bucket - so it stays on the count and
  // keeps the limitation its own copy states.
  if (instant === 'opening') return classify(countShare, at, 'count', null);

  /**
   * The base is empty only when the population is KNOWN to be zero - not merely when nothing proves otherwise.
   *
   * I had this the other way round one commit ago, on the argument that a failed request lands here and a
   * banner on the failure screen would be noise. That argument was about the SCREEN, and it belongs there: the
   * notes are now suppressed on failure, like the series note beside them already was. Here, "nobody told us"
   * and "there is nobody" are the two facts this whole refactor exists to separate, and collapsing them again
   * inside the resolver would have made the null pointless. Codex, #267.
   */
  /**
   * A broken MRR is withheld before anything else is asked about it.
   *
   * Every verdict below describes how much of a figure is covered, which presumes the figure exists. A negative
   * or non-numeric one is not a figure with poor coverage - there is nothing to state a share of.
   */
  if (!Number.isFinite(snapshot.mrr) || snapshot.mrr < 0)
    return { kind: 'unusable' };

  const population = resolvePopulation(snapshot, coverage);
  const knownEmpty = population.known && population.value === 0;

  const valueShare = valueShareOf(coverage.current, snapshot, knownEmpty);

  /**
   * Unmeasurable in money declares, unless the count is already saying something louder.
   *
   * Falling back to the count share alone would be the old behaviour, and the old behaviour is what the missing
   * field would then silently restore - a screen that looks identical whether the API answered with the money or
   * not. Below the thresholds the count still decides, because those verdicts are MORE disclosing than this one,
   * never less.
   */
  if (valueShare === null) {
    const byCount = classify(countShare, at, 'count', null);

    return byCount.kind === 'full'
      ? { kind: 'unpriceable', covered: at.covered, total: at.total }
      : byCount;
  }

  /**
   * The MONEY question is answered by the money figure alone, and the count share does not get a vote.
   *
   * This started as `Math.min(countShare, valueShare)` - a hedge, on the theory that the lower share is always the
   * more honest one. It is not, and the counter-example is exact: `uncoveredValue` is an upper BOUND, so an
   * explicit zero proves nothing was omitted. Pair that with 10 of 100 subscriptions attributed - a base of
   * courtesy subscriptions, which carry no charge - and the minimum withheld a figure the data proves complete,
   * while the note said at most R$ 0 was missing. Two contradicting claims, and the hedge produced both.
   *
   * The two shares are not two measurements of one thing. The count measures ATTRIBUTABILITY - how many
   * subscriptions the report could place - and the value measures MONEY. This note is about money: whether the
   * MRR beside it is understated, and by how much. A subscription that contributes nothing cannot understate it.
   *
   * What this leans on is the API's guarantee of DIRECTION: its covered test is stricter than the MRR's own
   * scope, so anything ambiguous is counted as missing. Trusting that is the whole point of #76 - the earlier
   * attempt was unshippable precisely because its errors ran the other way, towards silence.
   *
   * Codex, #267.
   */
  return classify(
    valueShare,
    at,
    'value',
    coverage.current.uncoveredValue ?? null,
  );
};

/**
 * The same three-way verdict for a single row of the series.
 *
 * A row is judged on ITS OWN base, and by the same thresholds, because the reason to withhold does not change
 * with the scope: a bucket priced for a tenth of its subscribers prints an MRR that is indistinguishable from a
 * month the community collapsed. Marking it and printing it anyway was the first version of this, and it left the
 * range-level figure withheld at 40% while a row at 10% showed a number.
 */
export const resolveRowCoverage = (row: RecurringRevenueRow) =>
  resolveMrrCoverage(
    {
      // Rows carry no pricing flag of their own; the series-level one is said once, above the table.
      pricedFromPeriods: true,
      opening: {
        covered: row.coveredSubscribers,
        total: row.activeSubscribers,
      },
      current: {
        covered: row.coveredSubscribers,
        total: row.activeSubscribers,
      },
    },
    // `opening`, so this is judged on the count and the snapshot below is never read. A row has no value share
    // to judge by: the API cannot attribute an unpriced subscription to a bucket, so there is no uncovered value
    // per period to compare against.
    'opening',
    { mrr: 0, activeSubscribers: 0 },
  );

/**
 * Whether one row of the series has to carry a marker of its own.
 *
 * Per row and not only at the ends of the range, because the ends cannot see it: a subscription with no known
 * period that both arrives and churns inside the window is absent from both edges, so `opening` and `current`
 * can each read complete while a bucket in the middle is missing value.
 *
 * Deliberately NOT gated on pricedFromPeriods. In the unpriced state the screen says so once, for the whole
 * series, and a per-row marker on top of that would repeat the same fact on every line.
 */
export const isRowCoverageDeclarable = (row: RecurringRevenueRow) =>
  resolveRowCoverage(row).kind !== 'full';

/** "94,5%" - the share as a producer reads it, one decimal, never rounded up to a claim of completeness. */
export const formatCoverageShare = (share: number) =>
  `${(Math.floor(share * 1000) / 10).toFixed(1)}%`;
